/**
 * Estado de sesión.
 *
 * El token vive solo en memoria (en el módulo del cliente HTTP); aquí guardamos
 * el usuario y el estado de arranque. `bootstrap()` corre una vez al montar la
 * app: intenta canjear la cookie de refresco por un token nuevo, de modo que
 * recargar la página no obliga a volver a iniciar sesión.
 */

import { create } from 'zustand';

import { setAccessToken, setSessionExpiredHandler } from '@/api/client';
import { authApi, type LoginPayload, type RegisterPayload } from '@/api/endpoints';
import type { UserMe } from '@/types/api';

interface AuthState {
  user: UserMe | null;
  /** `true` hasta que se resuelve el primer intento de restaurar la sesión. */
  isBootstrapping: boolean;
  isAuthenticated: boolean;
  bootstrap: () => Promise<void>;
  login: (payload: LoginPayload) => Promise<UserMe>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserMe) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isBootstrapping: true,
  isAuthenticated: false,

  bootstrap: async () => {
    try {
      const { access_token } = await authApi.refresh();
      setAccessToken(access_token);
      const user = await authApi.me();
      set({ user, isAuthenticated: true });
    } catch {
      // Sin cookie válida: simplemente no hay sesión que restaurar.
      setAccessToken(null);
      set({ user: null, isAuthenticated: false });
    } finally {
      set({ isBootstrapping: false });
    }
  },

  login: async (payload) => {
    const { access_token } = await authApi.login(payload);
    setAccessToken(access_token);
    const user = await authApi.me();
    set({ user, isAuthenticated: true });
    return user;
  },

  register: async (payload) => {
    await authApi.register(payload);
  },

  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      setAccessToken(null);
      set({ user: null, isAuthenticated: false });
    }
  },

  setUser: (user) => set({ user }),
}));

// Si el refresco falla a mitad de una petición, se cierra la sesión en el store.
setSessionExpiredHandler(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});
